import { FormFeedBack, MainButton, PageWrapper, STATUSPAGE } from '@repo/ui';
import { useFormik } from 'formik';
import { NumericDecimal } from 'presentation/app/components/inputs/numeric-decimal';
import { loadQuestionOptions } from 'presentation/config/store/evaluation-criteria/question-options/question-options-slice';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Col, Row, Form, Input, Card, Label, CardBody } from 'reactstrap';
import * as Yup from 'yup';

import { useQuestionOptions } from '../../hooks/useQuestionOptions';
import { ModalQuestionOptionsForm } from '../modals/modal-question-options-form';
import { TableDraggableEvaluationCriteria } from '../table-draggable-evaluation-criteria';

export const QuestionOptionsForm = () => {
  const [isModalEditOpen, setIsModalEditOpen] = useState(false);

  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const questionTitle = location?.state?.title;

  const dispatch = useDispatch();

  const {
    questionOptions,
    requestDeleteQuestionOption,
    requestGetQuestionOptionById,
    requestCreateQuestionOption,
    requestGetAllQuestionOptions,
    requestReorderQuestionOptions,
    createOrUpdateStatus,
  } = useQuestionOptions();

  function toggleModalEdit() {
    setIsModalEditOpen(!isModalEditOpen);
  }

  const handleEdit = (id: number) => {
    requestGetQuestionOptionById({ id });
    toggleModalEdit();
  };

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      grade: '',
      title: '',
      description: '',
    },
    validationSchema: Yup.object({
      title: Yup.string().required(),
      grade: Yup.string().nullable(),
      description: Yup.string().nullable(),
    }),
    onSubmit: async (values) => {
      const valuesToSubmit = {
        questionId: Number(id),
        name: values.title,
        grade: values.grade === '' ? null : Number(values.grade),
        description: values.description,
      };

      requestCreateQuestionOption(valuesToSubmit);
      validation.resetForm();
    },
  });

  useEffect(() => {
    requestGetAllQuestionOptions({ id: Number(id) });
    return () => {
      dispatch(loadQuestionOptions([]));
    };
  }, []);

  return (
    <PageWrapper title={`Opções de ${questionTitle}`}>
      <h4 className="mb-3">Opções de {questionTitle}</h4>

      <Form
        onSubmit={(e) => {
          e.preventDefault();
          validation.handleSubmit();
          return false;
        }}
      >
        <Card>
          <CardBody className="p-4">
            <Link
              onClick={() => navigate(-1)}
              className="link-primary link-offset-2 text-decoration-underline link-underline-opacity-25 link-underline-opacity-100-hover d-flex justify-content-end"
              to={''}
            >
              <i className="ri-arrow-left-line"></i>
              Voltar
            </Link>
            <Row className="g-4">
              <Col md={8}>
                <Label>Título</Label>
                <Input
                  value={validation.values.title}
                  onBlur={validation.handleBlur}
                  onChange={validation.handleChange}
                  name="title"
                  invalid={
                    !!(validation.touched.title && validation.errors.title)
                  }
                />
              </Col>

              <Col md={4}>
                <Label>Nota</Label>
                <NumericDecimal
                  id="grade"
                  name="grade"
                  allowNegative={false}
                  value={validation.values.grade}
                  onBlur={validation.handleBlur}
                  onChange={validation.handleChange}
                />
                <FormFeedBack
                  error={validation.errors.grade as any}
                  isInvalid={
                    validation.touched.grade && !!validation.errors.grade
                  }
                />
              </Col>

              <Col md={12}>
                <Label>Descrição</Label>
                <Input
                  value={validation.values.description}
                  onBlur={validation.handleBlur}
                  onChange={validation.handleChange}
                  name="description"
                  type={'textarea'}
                />
                <FormFeedBack
                  error={validation.errors.description as any}
                  isInvalid={
                    validation.touched.description &&
                    !!validation.errors.description
                  }
                />
              </Col>

              <Col
                md={12}
                className="d-flex justify-content-end align-items-center"
              >
                <MainButton type="submit"
                  isDisabled={createOrUpdateStatus === STATUSPAGE.LOADING}
                  isLoading={createOrUpdateStatus === STATUSPAGE.LOADING}
                >
                  <i className="ri-add-line"></i>
                  <span className="me-2">Adicionar</span>
                </MainButton>
              </Col>
            </Row>

            {questionOptions.length > 0 && (
              <Row className="mt-3">
                <TableDraggableEvaluationCriteria
                  requestType="question-options"
                  requestReorder={requestReorderQuestionOptions}
                  handleDelete={(id) => requestDeleteQuestionOption({ id })}
                  handleEdit={handleEdit}
                  data={questionOptions || []}
                  columns={[
                    {
                      header: 'Opção',
                      key: 'name',
                      render: (row) => (
                        <div>
                          <p className="fw-semibold m-0">{row.name}</p>
                          {row.description && (
                            <p className="text-muted m-0 fw-medium">
                              <i className="ri-information-line me-2 text-info"></i>
                              <em>{row.description}</em>
                            </p>
                          )}
                        </div>
                      ),
                    },
                    { header: 'Nota', key: 'grade' },
                  ]}
                />
              </Row>
            )}
          </CardBody>
        </Card>
      </Form>

      <ModalQuestionOptionsForm
        isOpen={isModalEditOpen}
        toggle={toggleModalEdit}
      />
    </PageWrapper>
  );
};
